import { Injectable } from '@angular/core';
import { FuncionarioBiometriaLocal, ServicoCacheBiometricoLocal } from './servico-cache-biometrico-local';

export interface ResultadoIdentificacaoBiometricaLocal {
  identificado: boolean;
  funcionario?: FuncionarioBiometriaLocal;
  motivo?: string;
  distanciaMelhor?: number;
  distanciaSegundo?: number;
  margem?: number;
}

@Injectable({ providedIn: 'root' })
export class ServicoIdentificacaoBiometricaLocal {
  private readonly limiteDistanciaFacial = 0.45;
  private readonly limiteAmbiguidadeAbsoluta = 0.12;
  private readonly limiteAmbiguidadeRelativa = 1.25;

  constructor(private readonly servicoCacheBiometricoLocal: ServicoCacheBiometricoLocal) {}

  async identificar(descritorFacial: number[], tokenTerminal: string): Promise<ResultadoIdentificacaoBiometricaLocal> {
    if (!descritorFacial.length) {
      return { identificado: false, motivo: 'Descritor facial nao informado.' };
    }

    const funcionarios = await this.servicoCacheBiometricoLocal.listarFuncionariosValidos(tokenTerminal);
    if (!funcionarios.length) {
      return { identificado: false, motivo: 'Cache biometrico offline vazio ou expirado.' };
    }

    const melhoresPorFuncionario = new Map<string, { funcionario: FuncionarioBiometriaLocal; distancia: number }>();
    funcionarios
      .filter(funcionario => funcionario.descritorFacial?.length === descritorFacial.length)
      .forEach(funcionario => {
        const distancia = this.distanciaEuclidiana(descritorFacial, funcionario.descritorFacial);
        const atual = melhoresPorFuncionario.get(funcionario.funcionarioId);
        if (!atual || distancia < atual.distancia) {
          melhoresPorFuncionario.set(funcionario.funcionarioId, { funcionario, distancia });
        }
      });

    const candidatos = Array.from(melhoresPorFuncionario.values())
      .sort((a, b) => a.distancia - b.distancia);

    const melhor = candidatos[0];
    if (!melhor) {
      return { identificado: false, motivo: 'Cache biometrico offline incompativel.' };
    }

    const segundo = candidatos[1];
    const margem = segundo ? segundo.distancia - melhor.distancia : undefined;
    if (melhor.distancia > this.limiteDistanciaFacial) {
      return {
        identificado: false,
        motivo: 'Servidor nao encontrado no cache offline.',
        distanciaMelhor: melhor.distancia,
        distanciaSegundo: segundo?.distancia,
        margem
      };
    }

    const ambiguo = !!segundo
      && (margem === undefined
        || margem < this.limiteAmbiguidadeAbsoluta
        || segundo.distancia / Math.max(melhor.distancia, 0.00001) < this.limiteAmbiguidadeRelativa);

    if (ambiguo) {
      return {
        identificado: false,
        motivo: 'Reconhecimento offline ambiguo. Conecte a internet e tente novamente.',
        distanciaMelhor: melhor.distancia,
        distanciaSegundo: segundo.distancia,
        margem
      };
    }

    return {
      identificado: true,
      funcionario: melhor.funcionario,
      distanciaMelhor: melhor.distancia,
      distanciaSegundo: segundo?.distancia,
      margem
    };
  }

  private distanciaEuclidiana(a: number[], b: number[]) {
    return Math.sqrt(a.reduce((total, valor, indice) => {
      const diferenca = valor - b[indice];
      return total + diferenca * diferenca;
    }, 0));
  }
}
